import React, { useState, useMemo } from "react";
import { Calculator, DollarSign, FileText, Info } from "lucide-react";
import { Link } from "react-router-dom";

interface TaxBracket {
  min: number;
  max: number;
  rate: number;
  cumulativeTax: number;
}

interface TaxBreakdown {
  grossIncome: number;
  consolidatedRelief: number;
  pensionContribution: number;
  nhfContribution: number;
  taxableIncome: number;
  annualTax: number;
  monthlyTax: number;
  netAnnualIncome: number;
  netMonthlyIncome: number;
  effectiveRate: number;
  breakdown: Array<{
    bracket: string;
    taxableAmount: number;
    rate: number;
    tax: number;
  }>;
}

const NigeriaTaxCalculator: React.FC = () => {
  const [income, setIncome] = useState<string>("");
  const [incomeType, setIncomeType] = useState<"monthly" | "annual">("monthly");
  const [pensionRate, setPensionRate] = useState<number>(8);
  const [includePension, setIncludePension] = useState<boolean>(true);
  const [includeNHF, setIncludeNHF] = useState<boolean>(true);

  // 2025 Nigerian PAYE Tax Brackets (in Naira) - Applied after ₦1.2M exemption

  const results = useMemo(() => {
    const taxBrackets: TaxBracket[] = [
      { min: 0, max: 300000, rate: 7, cumulativeTax: 0 },
      { min: 300000, max: 600000, rate: 11, cumulativeTax: 21000 },
      { min: 600000, max: 1100000, rate: 15, cumulativeTax: 54000 },
      { min: 1100000, max: 1600000, rate: 19, cumulativeTax: 129000 },
      { min: 1600000, max: 3200000, rate: 21, cumulativeTax: 224000 },
      { min: 3200000, max: Infinity, rate: 24, cumulativeTax: 560000 },
    ];
    const calculateTax = (annualGrossIncome: number): TaxBreakdown => {
      // Tax-free allowance: First ₦1,000,000 + ₦200,000 rent relief = ₦1,200,000
      const taxFreeAllowance = 1200000;

      // Consolidated Relief Allowance (CRA) - This is in addition to the tax-free allowance
      // 1% of gross income or ₦200,000 + 20% of gross income, whichever is higher
      const craOption1 = annualGrossIncome * 0.01;
      const craOption2 = 200000 + annualGrossIncome * 0.2;
      const consolidatedRelief = Math.max(craOption1, craOption2);

      // Pension contribution (optional)
      const pensionContribution = includePension
        ? annualGrossIncome * (pensionRate / 100)
        : 0;

      // National Housing Fund (2.5% for incomes above ₦3,000 monthly)
      const nhfContribution =
        includeNHF && annualGrossIncome > 36000
          ? Math.min(annualGrossIncome * 0.025, 50000)
          : 0; // Capped at ₦50,000

      // Calculate taxable income after all deductions
      const afterDeductions = Math.max(
        0,
        annualGrossIncome -
          consolidatedRelief -
          pensionContribution -
          nhfContribution,
      );

      // Apply tax-free allowance of ₦1.2M
      const taxableIncome = Math.max(0, afterDeductions - taxFreeAllowance);

      // If taxable income is 0, no tax is due
      if (taxableIncome <= 0) {
        return {
          grossIncome: annualGrossIncome,
          consolidatedRelief,
          pensionContribution,
          nhfContribution,
          taxableIncome: 0,
          annualTax: 0,
          monthlyTax: 0,
          netAnnualIncome: annualGrossIncome,
          netMonthlyIncome: annualGrossIncome / 12,
          effectiveRate: 0,
          breakdown: [],
        };
      }

      // Calculate tax using brackets on the amount above ₦1.2M
      let annualTax = 0;
      const breakdown: Array<{
        bracket: string;
        taxableAmount: number;
        rate: number;
        tax: number;
      }> = [];

      for (let i = 0; i < taxBrackets.length; i++) {
        const bracket = taxBrackets[i];
        const bracketMin = bracket.min;
        const bracketMax =
          bracket.max === Infinity
            ? taxableIncome
            : Math.min(bracket.max, taxableIncome);

        if (taxableIncome > bracketMin) {
          const taxableAmount = Math.max(0, bracketMax - bracketMin);
          const bracketTax = taxableAmount * (bracket.rate / 100);

          if (taxableAmount > 0) {
            annualTax += bracketTax;
            breakdown.push({
              bracket:
                bracket.max === Infinity
                  ? `₦${(
                      bracketMin + taxFreeAllowance
                    ).toLocaleString()} and above`
                  : `₦${(bracketMin + taxFreeAllowance).toLocaleString()} - ₦${(
                      bracket.max + taxFreeAllowance
                    ).toLocaleString()}`,
              taxableAmount,
              rate: bracket.rate,
              tax: bracketTax,
            });
          }
        }

        if (taxableIncome <= bracket.max) break;
      }

      const monthlyTax = annualTax / 12;
      const netAnnualIncome = annualGrossIncome - annualTax;
      const netMonthlyIncome = netAnnualIncome / 12;
      const effectiveRate =
        annualGrossIncome > 0 ? (annualTax / annualGrossIncome) * 100 : 0;

      return {
        grossIncome: annualGrossIncome,
        consolidatedRelief,
        pensionContribution,
        nhfContribution,
        taxableIncome,
        annualTax,
        monthlyTax,
        netAnnualIncome,
        netMonthlyIncome,
        effectiveRate,
        breakdown,
      };
    };

    const numericIncome = parseFloat(income.replace(/,/g, "")) || 0;
    const annualIncome =
      incomeType === "monthly" ? numericIncome * 12 : numericIncome;
    return calculateTax(annualIncome);
  }, [includeNHF, includePension, income, incomeType, pensionRate]);

  const formatCurrency = (amount: number): string => {
    return `₦${amount.toLocaleString("en-NG", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const handleIncomeChange = (value: string) => {
    // Remove non-numeric characters except decimal point
    const cleaned = value.replace(/[^\d.]/g, "");
    // Format with commas
    const formatted = cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    setIncome(formatted);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="w-12 h-12 text-green-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">
              Nigeria Tax Calculator 2025
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Calculate your Nigerian Personal Income Tax (PAYE) based on current
            rates
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <DollarSign className="w-6 h-6 mr-2 text-green-600" />
              Income Details
            </h2>

            <div className="space-y-6">
              {/* Income Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Income Period
                </label>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setIncomeType("monthly")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      incomeType === "monthly"
                        ? "bg-green-600 text-white shadow-sm"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setIncomeType("annual")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      incomeType === "annual"
                        ? "bg-green-600 text-white shadow-sm"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Annual
                  </button>
                </div>
              </div>

              {/* Income Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {incomeType === "monthly" ? "Monthly" : "Annual"} Gross Income
                  (₦)
                </label>
                <input
                  type="text"
                  value={income}
                  onChange={(e) => handleIncomeChange(e.target.value)}
                  placeholder="Enter your income..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                />
              </div>

              {/* Pension Contribution */}
              <div>
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={includePension}
                    onChange={(e) => setIncludePension(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Include Pension Contribution
                  </span>
                </label>
                {includePension && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pension Contribution Rate (%)
                    </label>
                    <input
                      type="number"
                      value={pensionRate}
                      onChange={(e) =>
                        setPensionRate(parseFloat(e.target.value) || 0)
                      }
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* NHF Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeNHF}
                    onChange={(e) => setIncludeNHF(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Include National Housing Fund (2.5%, max ₦50,000)
                  </span>
                </label>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Tax Calculation Notes:</p>
                    <ul className="text-xs space-y-1">
                      <li>
                        • First ₦1.2M annually is tax-free (₦1M + ₦200K rent
                        relief)
                      </li>
                      <li>• Tax brackets apply only to income above ₦1.2M</li>
                      <li>• Includes Consolidated Relief Allowance (CRA)</li>
                      <li>• Standard pension contribution is 8% (optional)</li>
                      <li>
                        • NHF applies to monthly incomes above ₦3,000 (optional)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-green-600" />
              Tax Calculation Results
            </h2>

            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">
                    Annual Tax
                  </p>
                  <p className="text-xl font-bold text-green-800">
                    {formatCurrency(results.annualTax)}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">
                    Monthly Tax
                  </p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatCurrency(results.monthlyTax)}
                  </p>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-800">
                    Income Breakdown
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gross Annual Income:</span>
                    <span className="font-semibold">
                      {formatCurrency(results.grossIncome)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      - Consolidated Relief:
                    </span>
                    <span className="text-red-600">
                      ({formatCurrency(results.consolidatedRelief)})
                    </span>
                  </div>
                  {results.pensionContribution > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        - Pension Contribution:
                      </span>
                      <span className="text-red-600">
                        ({formatCurrency(results.pensionContribution)})
                      </span>
                    </div>
                  )}
                  {results.nhfContribution > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">- NHF Contribution:</span>
                      <span className="text-red-600">
                        ({formatCurrency(results.nhfContribution)})
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">- Tax-Free Allowance:</span>
                    <span className="text-red-600">
                      ({formatCurrency(1200000)})
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Taxable Income:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.taxableIncome)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-800 font-semibold">
                      Annual Tax:
                    </span>
                    <span className="font-bold text-green-600">
                      {formatCurrency(results.annualTax)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Effective Tax Rate:</span>
                    <span className="font-semibold">
                      {results.effectiveRate.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Net Income */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-green-100 text-sm">Net Annual Income</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(results.netAnnualIncome)}
                    </p>
                  </div>
                  <div>
                    <p className="text-green-100 text-sm">Net Monthly Income</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(results.netMonthlyIncome)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tax Bracket Breakdown */}
              {results.breakdown.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-800">
                      Tax Bracket Breakdown
                    </h3>
                  </div>
                  <div className="p-4">
                    {results.breakdown.map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {item.bracket}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatCurrency(item.taxableAmount)} @ {item.rate}%
                          </p>
                        </div>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(item.tax)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Promotional Section */}
        <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">
            Want to Make Money Selling Your Knowledge in 2026?
          </h2>
          <p className="text-lg text-green-50 mb-6 max-w-3xl mx-auto">
            This is how busy salary earners are making at least ₦1M naira extra
            income without quitting their jobs.
          </p>
          <Link
            to="/dbc/free-training"
            className="inline-block bg-white text-green-700 font-semibold px-8 py-4 rounded-lg hover:bg-green-50 transition-colors shadow-lg text-lg"
          >
            Click Here
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>
            Tax calculations are based on 2025 Nigerian tax rates and may not
            include all applicable deductions. Consult a tax professional for
            accurate advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NigeriaTaxCalculator;
